import { useRef } from 'react';
import classNames from 'classnames';
import { PhotoSize } from '@alltrails/shared/types/photos';
import { getPhotoUrl } from '@alltrails/shared/utils/requests/photoRequests';
import { ServerCommunicationUtil } from 'utils/server_communication_util';
import { Photo } from 'types/Photo';
import * as styles from './styles.module.scss';
import getSizeClass from './getSizeClass';

export type PhotosUgcItemProps = {
  item: Photo;
  rel?: string;
  idx: number;
  openLightbox: (idx: number) => void;
  photoSize?: PhotoSize;
  dynamicPhotoSizes?: boolean;
  trailName?: string;
};

const PhotosUgcItem = ({
  item,
  idx,
  photoSize = PhotoSize.LargeSquare,
  rel,
  openLightbox,
  dynamicPhotoSizes,
  trailName
}: PhotosUgcItemProps): JSX.Element => {
  const imgRef = useRef<HTMLImageElement>();

  const { id, photoHash, locationName, trailName: itemTrailName } = item;
  const alt = [itemTrailName, locationName].filter(part => !!part).join(' - ') || [trailName, idx].filter(part => part !== undefined).join(' - ');
  const fallbackPhotoUrl = `/api/alltrails/photos/${id}/image?size=${photoSize}&api_key=${ServerCommunicationUtil.apiKey}`;
  const fallbackBigPhotoUrl = `/api/alltrails/photos/${id}/image?size=extra_large&api_key=${ServerCommunicationUtil.apiKey}`;
  let photoUrl;
  let bigPhotoUrl;
  if (id && photoHash) {
    photoUrl = getPhotoUrl({ id, photoHash }, photoSize);
    bigPhotoUrl = getPhotoUrl({ id, photoHash }, PhotoSize.ExtraLarge);
  } else {
    photoUrl = fallbackPhotoUrl;
    bigPhotoUrl = fallbackBigPhotoUrl;
  }
  let shouldFallback = true;
  const addFallbackSrc = (ev: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const elt = ev.target as HTMLImageElement;
    if (shouldFallback) {
      elt.src = fallbackPhotoUrl;
      elt.onerror = null;
    }
    shouldFallback = false;
  };

  const handlePhotoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    openLightbox(idx);
  };

  return (
    <a
      className={classNames(getSizeClass(photoSize, idx, dynamicPhotoSizes), {
        [styles.photoItem]: !dynamicPhotoSizes,
        [styles.photoItemDynamic]: dynamicPhotoSizes
      })}
      data-credit-link=""
      data-photo-id={id}
      key={`trail-photo-${id}`}
      onClick={e => handlePhotoClick(e)}
      href={bigPhotoUrl}
      rel={rel}
    >
      <img alt={alt} className="lazyload" id={`trail-photo-${id}`} onError={addFallbackSrc} ref={imgRef} data-src={photoUrl} />
    </a>
  );
};

export default PhotosUgcItem;
